/**
 * Export Quality Tiers for Video Exports
 *
 * Defines quality tiers for Shotstack video rendering with plan-based gating.
 * Quality tiers determine output resolution and processing requirements.
 */

export type ExportQuality = 'sd' | 'hd' | '1080' | '4k';
export type PlanType = 'free' | 'plus' | 'pro' | 'enterprise';

export interface QualityTier {
  id: ExportQuality;
  name: string;
  label: string;
  description: string;
  resolution: {
    width: number;
    height: number;
    label: string;
  };
  requiredPlans: PlanType[];
  badge?: {
    text: string;
    variant: 'default' | 'secondary' | 'outline' | 'destructive';
  };
  icon: string;
  estimatedFileSize?: string;
  processingTime?: string;
}

/**
 * Quality tier definitions
 */
export const QUALITY_TIERS: Record<ExportQuality, QualityTier> = {
  sd: {
    id: 'sd',
    name: 'Standard Definition',
    label: '480p SD',
    description: 'Standard quality - Best for quick previews and sharing',
    resolution: {
      width: 854,
      height: 480,
      label: '480p'
    },
    requiredPlans: ['plus', 'pro', 'enterprise'],
    badge: {
      text: 'Plus',
      variant: 'default'
    },
    icon: '📱',
    estimatedFileSize: '~5-10 MB/min',
    processingTime: '~1-2 min'
  },

  hd: {
    id: 'hd',
    name: 'High Definition',
    label: '720p HD',
    description: 'HD quality - Perfect for social media and presentations',
    resolution: {
      width: 1280,
      height: 720,
      label: '720p'
    },
    requiredPlans: ['plus', 'pro', 'enterprise'],
    badge: {
      text: 'Plus',
      variant: 'default'
    },
    icon: '🎬',
    estimatedFileSize: '~15-25 MB/min',
    processingTime: '~2-4 min'
  },

  '1080': {
    id: '1080',
    name: 'Full HD',
    label: '1080p Full HD',
    description: 'Full HD quality - Professional presentations and content',
    resolution: {
      width: 1920,
      height: 1080,
      label: '1080p'
    },
    requiredPlans: ['plus', 'pro', 'enterprise'],
    badge: {
      text: 'Plus',
      variant: 'default'
    },
    icon: '🎥',
    estimatedFileSize: '~30-50 MB/min',
    processingTime: '~4-6 min'
  },

  '4k': {
    id: '4k',
    name: 'Ultra HD 4K',
    label: '4K Ultra HD',
    description: 'Maximum quality - Premium content and client deliverables',
    resolution: {
      width: 3840,
      height: 2160,
      label: '4K'
    },
    requiredPlans: ['pro', 'enterprise'],
    badge: {
      text: 'Pro',
      variant: 'default'
    },
    icon: '⭐',
    estimatedFileSize: '~100-200 MB/min',
    processingTime: '~10-15 min'
  }
};

/**
 * Get quality tier configuration
 */
export function getQualityTier(quality: ExportQuality): QualityTier {
  return QUALITY_TIERS[quality];
}

/**
 * Get all available quality tiers
 */
export function getAvailableQualityTiers(): QualityTier[] {
  return Object.values(QUALITY_TIERS);
}

/**
 * Check if a quality tier is available for a plan
 */
export function isQualityAvailableForPlan(
  quality: ExportQuality,
  plan: PlanType
): boolean {
  const tier = QUALITY_TIERS[quality];
  return tier.requiredPlans.includes(plan);
}

/**
 * Get the maximum quality available for a plan
 */
export function getMaxQualityForPlan(plan: PlanType): ExportQuality {
  const qualities: ExportQuality[] = ['4k', '1080', 'hd', 'sd'];

  for (const quality of qualities) {
    if (isQualityAvailableForPlan(quality, plan)) {
      return quality;
    }
  }

  return 'sd'; // Fallback to SD
}

/**
 * Get default quality for a plan
 */
export function getDefaultQualityForPlan(plan: PlanType): ExportQuality {
  switch (plan) {
    case 'pro':
    case 'enterprise':
      return '4k';
    case 'plus':
      return 'hd';
    case 'free':
    default:
      return 'hd'; // Default to HD even for free (will be blocked by plan check)
  }
}

/**
 * Get quality tiers filtered by plan access
 */
export function getQualityTiersForPlan(plan: PlanType): {
  available: QualityTier[];
  locked: QualityTier[];
} {
  const available: QualityTier[] = [];
  const locked: QualityTier[] = [];

  Object.values(QUALITY_TIERS).forEach(tier => {
    if (tier.requiredPlans.includes(plan)) {
      available.push(tier);
    } else {
      locked.push(tier);
    }
  });

  return { available, locked };
}

/**
 * Get upgrade message for locked quality tier
 */
export function getQualityUpgradeMessage(
  quality: ExportQuality,
  currentPlan: PlanType
): string {
  const tier = QUALITY_TIERS[quality];
  const requiredPlan = tier.requiredPlans.find(p =>
    p !== 'free' && p !== currentPlan
  );

  if (!requiredPlan) {
    return 'Upgrade required for this quality';
  }

  const planName = requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1);
  return `${tier.label} requires ${planName} plan`;
}

/**
 * Validate quality selection for plan
 */
export function validateQualityForPlan(
  quality: ExportQuality,
  plan: PlanType
): {
  valid: boolean;
  error?: string;
  upgradeRequired?: boolean;
  requiredPlan?: string;
} {
  const tier = QUALITY_TIERS[quality];

  if (tier.requiredPlans.includes(plan)) {
    return { valid: true };
  }

  const requiredPlan = tier.requiredPlans
    .filter(p => p !== 'free')
    .sort((a, b) => {
      const order = { plus: 1, pro: 2, enterprise: 3 };
      return order[a as keyof typeof order] - order[b as keyof typeof order];
    })[0];

  return {
    valid: false,
    error: getQualityUpgradeMessage(quality, plan),
    upgradeRequired: true,
    requiredPlan: requiredPlan?.charAt(0).toUpperCase() + requiredPlan?.slice(1)
  };
}

/**
 * Get quality tier options for UI selectors
 */
export interface QualityOption {
  value: ExportQuality;
  label: string;
  description: string;
  resolution: string;
  badge?: string;
  icon: string;
  locked: boolean;
}

export function getQualityOptions(currentPlan: PlanType): QualityOption[] {
  return Object.values(QUALITY_TIERS).map(tier => ({
    value: tier.id,
    label: tier.label,
    description: tier.description,
    resolution: tier.resolution.label,
    badge: tier.badge?.text,
    icon: tier.icon,
    locked: !tier.requiredPlans.includes(currentPlan)
  }));
}

/**
 * Format resolution for display
 */
export function formatResolution(quality: ExportQuality): string {
  const tier = QUALITY_TIERS[quality];
  return `${tier.resolution.width}x${tier.resolution.height}`;
}

/**
 * Get Shotstack resolution value for API
 */
export function getShotstackResolution(quality: ExportQuality): string {
  const resolutionMap: Record<ExportQuality, string> = {
    sd: '480',
    hd: '720',
    '1080': '1080',
    '4k': '4k'
  };

  return resolutionMap[quality];
}
